import { useTranslation } from "react-i18next";
import { useTheme } from "antd-style";
import { useEffect, useState } from "react";
import { getInstanceTemplate } from "@/apis";
import { notification } from "antd";
import WrappedSelect from "@/components/instance/config/WrappedSelect";
import { requiredRule } from "./useBasicComponent";
import { useResourceContext } from "@/layouts/ResourceContext";

export const useTemplateComponent = ({ formRef, onValuesChangeCallback }) => {
  const { t } = useTranslation();
  const {
    rootState: { eks },
  } = useResourceContext();

  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateEnum, setTemplateEnum] = useState({});
  const [templateDisabled, setTemplateDisabled] = useState(false);

  useEffect(() => {
    const fetchContext = async () => {
      if (eks.isError) {
        setTemplateLoading(true);
        notification.error({
          description: t(
            "config.instance.error.context",
            "Fail to get context from server. Please refresh browser later",
          ),
        });
        return;
      }
      if (eks.isLoading) {
        setTemplateLoading(true);
        notification.error({
          description: t(
            "config.instance.error.contextLoading",
            "Loading context from server. Please re-launch instance later.",
          ),
        });
        return;
      }

      const tpltEnum = eks.data["instance-profiles"].reduce(
        (accumulator, item) => {
          accumulator[item] = { text: item };
          return accumulator;
        },
        {},
      );
      setTemplateEnum(tpltEnum);
      setTemplateLoading(false);
    };

    fetchContext();
  }, [JSON.stringify(eks)]);

  const loadTemplate = async (templateType) => {
    setTemplateLoading(true);
    setTemplateDisabled(true);
    formRef.current.setFieldsValue({
      ["template"]: templateType,
    });

    const data = await getInstanceTemplate(templateType);
    formRef.current.setFieldsValue({
      ["template-define"]: data,
    });

    if (data.status !== "OK") {
      notification.error({
        description: t(
          "config.instance.error.templateLoading",
          "Fail to load template from server. Please re-select template later.",
        ),
      });
    }
    setTemplateLoading(false);
    setTemplateDisabled(false);
  };

  useEffect(() => {
    const f = async () => await loadTemplate("small-non-ha");
    f();
  }, []);

  const templateComponent = () => ({
    i18nTitle: "config.instance.form.template",
    title: t("config.instance.form.template", "Template"),
    dataIndex: "template",
    renderFormItem: (item, _, form) => {
      return (
        <WrappedSelect
          width="400px"
          defaultValue="small-non-ha"
          options={templateEnum}
          loading={templateLoading}
          disabled={templateDisabled}
          onChange={async (newVariables) => {
            await loadTemplate(newVariables);
            onValuesChangeCallback({ template: newVariables });
          }}
        />
      );
    },
    formItemProps: {
      rules: [
        ...requiredRule(t),
        ({ getFieldValue }) => ({
          async validator(_, value) {
            const value1 = getFieldValue("template-define");
            if (value1 !== undefined && value1.status !== "OK") {
              Promise.reject(
                new Error(
                  t(
                    "config.instance.error.templateLoading",
                    "Fail to load template from server. Please re-select template later.",
                  ),
                ),
              );
            }
            return Promise.resolve();
          },
        }),
      ],
    },
    width: "400px",
  });

  return {
    templateComponent,
  };
};
